import {
  OrderStatus,
  SkuOrderStatus,
  SkuOrderStatusDto,
  SkuOrderStatusStatus,
} from '@/types/OrderStatusType';
import { SaleStatus } from '@/types/props/components/selling';
import {
  OfferAuth,
  OfferAuthDto,
  Order,
  PaymentIntent,
} from '@/types/skuDataBackend';
import { isValid, isDate } from 'date-fns';
import _sortBy from 'lodash/sortBy';

const skuOrderStatusDto = (
  SkuOrderStatus: SkuOrderStatusStatus
): SkuOrderStatusDto => {
  return {
    matched: new Date(SkuOrderStatus?.matched),
    matchedCancelled: new Date(SkuOrderStatus?.matchedCancelled),
    authenticated: new Date(SkuOrderStatus?.authenticated),
    authenticationCancelled: new Date(SkuOrderStatus?.authenticationCancelled),
    authenticationSoftRejected: new Date(
      SkuOrderStatus?.authenticationSoftRejected
    ),
    paid: new Date(SkuOrderStatus?.paid),
    paymentCancelled: new Date(SkuOrderStatus?.paymentCancelled),
    paymentTimeout: new Date(SkuOrderStatus?.paymentTimeout),
    payoutCreated: new Date(SkuOrderStatus?.payoutCreated),
    payoutDone: new Date(SkuOrderStatus?.payoutDone),
    payoutFailed: new Date(SkuOrderStatus?.payoutFailed),
    shippingLabelProvided: new Date(SkuOrderStatus?.shippingLabelProvided),
    recievedByBuyer: new Date(SkuOrderStatus?.recievedByBuyer),
    recievedByCarrier: new Date(SkuOrderStatus?.recievedByCarrier),
    recievedByTimeout: new Date(SkuOrderStatus?.recievedByTimeout),
    shippedByCarrier: new Date(SkuOrderStatus?.shippedByCarrier),
    shippedBySeller: new Date(SkuOrderStatus?.shippedBySeller),
    triggerPayout: new Date(SkuOrderStatus?.triggerPayout),
  };
};

const offerAuthDto = (sale: OfferAuth): OfferAuthDto => {
  return {
    created: sale?.created!,
    submitted: sale?.submitted!,
    rejected: sale?.rejected!,
  };
};

export const getLatestStatus = (
  SkuOrderStatus: SkuOrderStatusStatus
): keyof SkuOrderStatusStatus => {
  const dto = skuOrderStatusDto(SkuOrderStatus);
  const dates: { k: keyof SkuOrderStatusStatus; v: Date }[] = [];
  for (const [k, v] of Object.entries(dto)) {
    const kWithType = k as keyof SkuOrderStatusStatus;
    const is_date = isDate(new Date(v));
    const is_valid_date = isValid(new Date(v));
    if (v && is_date && is_valid_date) {
      const _v = new Date(v) as Date;
      dates.push({ k: kWithType, v: _v });
    }
  }

  const sorted = _sortBy(dates, 'v').reverse();
  if (sorted.length > 0) {
    return sorted[0].k;
  } else {
    return 'matched';
  }
};

export const getLatestSaleStatus = (
  latestStatus: keyof SkuOrderStatusStatus,
  hasPaymentIntents: boolean
): SaleStatus => {
  if (
    latestStatus === 'matched' ||
    latestStatus === 'authenticationSoftRejected' ||
    latestStatus === 'authenticationTimeout'
  ) {
    return 'authentication';
  }
  if (latestStatus === 'authenticated' && hasPaymentIntents) {
    return 'payment';
  }
  if (latestStatus === 'authenticated') {
    return 'authenticated';
  }
  if (latestStatus === 'paid') {
    return 'paid';
  }
  if (
    latestStatus === 'shippingLabelProvided' ||
    latestStatus === 'shippedByCarrier' ||
    latestStatus === 'shippedBySeller'
  ) {
    return 'shipping';
  }
  if (
    latestStatus === 'recievedByCarrier' ||
    latestStatus === 'triggerPayout' ||
    latestStatus === 'recievedByBuyer' ||
    latestStatus === 'recievedByTimeout' ||
    latestStatus === 'payoutCreated'
  ) {
    return 'payoutCreated';
  }

  if (latestStatus === 'payoutDone') {
    return 'payout';
  }

  return 'cancelled';
};

export const getLatestOrderStatus = (
  latestStatus: keyof SkuOrderStatusStatus,
  paymentIntents: PaymentIntent[]
): OrderStatus => {
  if (
    latestStatus === 'matched' ||
    latestStatus === 'authenticationSoftRejected'
  ) {
    return 'authentication';
  }
  if (latestStatus === 'authenticated' && paymentIntents.length > 0) {
    return 'payment';
  }
  if (latestStatus === 'authenticated') {
    return 'authenticated';
  }
  if (latestStatus === 'paid') {
    return 'paid';
  }
  if (
    latestStatus === 'shippingLabelProvided' ||
    latestStatus === 'shippedByCarrier' ||
    latestStatus === 'shippedBySeller'
  ) {
    return 'shipping';
  }

  if (
    latestStatus === 'recievedByCarrier' ||
    latestStatus === 'recievedByTimeout'
  ) {
    return 'confirm-received';
  }
  if (latestStatus === 'recievedByBuyer') {
    return 'received';
  }

  return 'cancelled';
};

export const getLatestOrderErrorStatus = (
  latestStatus: keyof SkuOrderStatusStatus
) => {
  if (
    latestStatus === 'authenticationCancelled' ||
    latestStatus === 'authenticationTimeout'
  ) {
    return 'authentication.error';
  }

  if (
    latestStatus === 'paymentCancelled' ||
    latestStatus === 'paymentTimeout'
  ) {
    return 'payment.error';
  }

  if (latestStatus === 'shippingShipmentFailed') {
    return 'shipping.error';
  }

  return 'matched.error';
};

export const getLatestAdminStatus = (sale: SkuOrderStatus | undefined) => {
  const dto = skuOrderStatusDto(sale!);
  const dates: { k: string; v: any }[] = [];
  for (const [k, v] of Object.entries(dto!)) {
    const is_date = isDate(new Date(v));
    const is_valid_date = isValid(new Date(v));
    if (v && is_date && is_valid_date) {
      const _v = new Date(v) as Date;
      dates.push({ k, v: _v });
    }
  }

  const sorted = _sortBy(dates, 'v').reverse();
  return sorted && sorted[0]?.k ? sorted[0].k : '';
};

export const getLatestAdminSubStatus = (sale: OfferAuth | undefined) => {
  const dto = offerAuthDto(sale!);
  const dates: { k: string; v: any }[] = [];
  for (const [k, v] of Object.entries(dto!)) {
    const is_date = isDate(new Date(v));
    const is_valid_date = isValid(new Date(v));
    if (v && is_date && is_valid_date) {
      const _v = new Date(v) as Date;
      dates.push({ k, v: _v });
    }
  }
  const sorted = _sortBy(dates, 'v').reverse();
  return sorted && sorted[0]?.k ? sorted[0].k : '';
};

export const getLatestStatusLabel = (status: string) => {
  let latestStatusLabel = '';
  if (status == 'matched') latestStatusLabel = 'Authentication';
  if (status == 'authenticated') latestStatusLabel = 'Payment';
  if (status == 'paid') latestStatusLabel = 'Shipping';
  if (status == 'shippingLabelProvided') latestStatusLabel = 'Shipping';
  if (status == 'recievedByCarrier' || status == 'triggerPayout')
    latestStatusLabel = 'Payout';
  if (status == 'payoutDone') latestStatusLabel = 'Completed';
  return latestStatusLabel;
};
